import React, { useState } from "react"
import { Button, Space, Typography } from "antd"
import { EditOutlined } from "@ant-design/icons"
import { IMoney } from "platform-client/types"
import { MoneyDisplay } from "components/display/MoneyDisplay"
import CellContentRateInput from "components/Console/CellContent/RateInput"
import {
  decimalToPercentage,
  percentageToDecimal
} from "utils/pocketknife/converters"

const COMMISSION_PRECISION = 2

const roundToPrecision = (value: number, precision: number): number => {
  const factor = 10 ** precision
  return Math.round(value * factor) / factor
}

// Mirrors CommissionEngine.ValidateOverrideBrokerCommission so the broker sees the problem
// as they type, rather than after a round trip. The backend still validates.
export const getBrokerCommissionError = (
  rawValue: string,
  maxPercent: number
): string | undefined => {
  if (rawValue.trim() === "") {
    return "Enter a commission rate."
  }

  const enteredPercent = Number(rawValue)

  if (Number.isNaN(enteredPercent)) {
    return "Enter a valid number."
  }

  if (enteredPercent < 0 || enteredPercent > maxPercent) {
    return `Commission must be between 0% and ${maxPercent}%.`
  }

  if (
    roundToPrecision(enteredPercent, COMMISSION_PRECISION) !== enteredPercent
  ) {
    return `Commission can have at most ${COMMISSION_PRECISION} decimal places.`
  }

  return undefined
}

export const BrokerCommissionAdjuster: React.FC<{
  amount: IMoney
  currentCommissionPercent?: number
  // The API defaults this for broker products, so it's only missing on quotes that
  // can't be flexed anyway - treated as read-only rather than guessing a max here.
  maxCommissionPercent?: number
  isSubmitting?: boolean
  // Omit onSubmit to show the percentage read-only, with no Adjust affordance -
  // e.g. quote types/states outside the window ST-882 allows flexing in.
  onSubmit?: (commissionPercent: number) => Promise<void>
}> = ({
  amount,
  currentCommissionPercent,
  maxCommissionPercent,
  isSubmitting,
  onSubmit
}) => {
  const [isAdjusting, setIsAdjusting] = useState(false)
  const [inputValue, setInputValue] = useState("")

  const canAdjust = onSubmit !== undefined && maxCommissionPercent !== undefined

  // decimalToPercentage is a plain * 100, so round away float noise (0.07 * 100 = 7.000000000000001)
  const maxPercent = roundToPrecision(
    decimalToPercentage(maxCommissionPercent ?? 0),
    COMMISSION_PRECISION
  )

  const validationError = getBrokerCommissionError(inputValue, maxPercent)

  const startAdjusting = () => {
    setInputValue(
      String(
        roundToPrecision(
          decimalToPercentage(currentCommissionPercent ?? 0),
          COMMISSION_PRECISION
        )
      )
    )
    setIsAdjusting(true)
  }

  // A form submit, so pressing Enter in the input saves as well as clicking Save.
  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!onSubmit || !canAdjust || validationError || isSubmitting) {
      return
    }

    // The backend rejects anything beyond 4dp as a fraction (2dp as a percentage), so strip
    // float noise from the / 100 (e.g. 5.7 / 100 = 0.057000000000000002).
    onSubmit(roundToPrecision(percentageToDecimal(Number(inputValue)), 4))
      .then(() => setIsAdjusting(false))
      .catch(() => {
        // Failure is already surfaced to the user by the caller.
      })
  }

  if (!isAdjusting) {
    return (
      <Space size="small">
        {(currentCommissionPercent !== undefined || canAdjust) && (
          <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
            (
            {currentCommissionPercent !== undefined &&
              `${roundToPrecision(
                decimalToPercentage(currentCommissionPercent),
                COMMISSION_PRECISION
              )}%`}
            {canAdjust && (
              <EditOutlined
                role="button"
                tabIndex={0}
                title="Adjust"
                aria-label="Adjust broker commission"
                onClick={startAdjusting}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    startAdjusting()
                  }
                }}
                style={{ cursor: "pointer", marginLeft: 4 }}
              />
            )}
            )
          </span>
        )}
        <MoneyDisplay value={amount} />
      </Space>
    )
  }

  return (
    <form onSubmit={handleSave} aria-label="Broker commission">
      <Space direction="vertical" size={4} style={{ alignItems: "flex-end" }}>
        <Space>
          <div style={{ width: 100 }}>
            <CellContentRateInput
              type="Percentage"
              precision={COMMISSION_PRECISION}
              value={inputValue}
              readOnly={isSubmitting}
              aria-label="Broker commission percentage"
              aria-invalid={Boolean(validationError)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setInputValue(event.target.value)
              }
            />
          </div>
          <Button
            type="primary"
            size="small"
            htmlType="submit"
            loading={isSubmitting}
            disabled={Boolean(validationError)}
          >
            Save
          </Button>
          <Button
            type="text"
            size="small"
            htmlType="button"
            disabled={isSubmitting}
            onClick={() => setIsAdjusting(false)}
          >
            Cancel
          </Button>
        </Space>
        <div role={validationError ? "alert" : undefined}>
          <Typography.Text
            type={validationError ? "danger" : "secondary"}
            style={{ fontSize: 12 }}
          >
            {validationError ?? `Max ${maxPercent}%`}
          </Typography.Text>
        </div>
      </Space>
    </form>
  )
}
